import { connect } from 'react-redux';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { Group, GroupUpdateReference } from '~/Api/User/ProductGroup/CzReimbursement';
import {
	Action as PriceReferencingAction,
	Module,
	pruneReferenceBasketVersions,
	ReferenceBasketVersion,
	selectReferenceBasketVersions,
} from '~/PriceReferencing';
import { State, ThunkAction } from '~/Store';
import { Status, Statuses } from '~/UI/SaveActivityIndicator/SaveActivityIndicator';
import { Action } from './Action';
import { fetchGroup, fetchReferenceBasketVersions, pruneCzReimbursement, updateGroup } from './actions';
import { GroupUpdate, selectGroup } from './reducer';
import Settings from './Settings';

function selectUpdateStatus(group: UserProductGroup, update: GroupUpdate | null): Status | null {
	if (!update || update?.groupId !== group.id) {
		return null;
	}

	switch (update.state.name) {
		case 'fetching':
			return Statuses.saving;
		case 'updated':
			return Statuses.saved;
		case 'failed':
			return Statuses.failed;
		default:
			return null;
	}
}

function mapStateToProps(
	state: State,
	props: { group: UserProductGroup }
): {
	czReimbursement: Group | null;
	updateStatus: Status | null;
	versions: ReferenceBasketVersion[] | null;
} {
	return {
		versions: selectReferenceBasketVersions(state.priceReferencing, Module.czReimbursement),
		updateStatus: selectUpdateStatus(props.group, state.czReimbursement.groupUpdate),
		czReimbursement: selectGroup(state.czReimbursement, props.group.id)?.group ?? null,
	};
}

const load =
	(groupId: string): ThunkAction<void, Action> =>
	async (dispatch) => {
		dispatch(fetchReferenceBasketVersions());
		dispatch(fetchGroup(groupId));
	};

const unload =
	(groupId: string): ThunkAction<void, Action | PriceReferencingAction> =>
	async (dispatch) => {
		dispatch(pruneReferenceBasketVersions());
		dispatch(pruneCzReimbursement(groupId));
	};

const update =
	(groupId: string, reference: GroupUpdateReference): ThunkAction<void, Action> =>
	async (dispatch) => {
		dispatch(
			updateGroup(groupId, (group) => ({
				reference,
				productLists: group.productLists.map((list) => ({
					id: list.productList.id,
					dosageStrength: list.dosageStrength,
					packageSize: list.packageSize,
					usualTherapeuticDailyDose: list.usualTherapeuticDailyDose,
					usualTherapeuticDailyDosePerPackage: list.usualTherapeuticDailyDosePerPackage,
					selected: list.selected,
				})),
			}))
		);
	};

const actions = {
	onLoad: load,
	onUnload: unload,
	onChange: update,
};

export default connect(mapStateToProps, actions)(Settings);
